import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Swal from "sweetalert2";
import "./Header.css";
import { useTheme } from "../../contexts/ThemeContext";
import api, { BASE_URL } from "../../services/api";

function Header({ onSidebarToggle, onRightPanelToggle, isDesktop, isSidebarOpen }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const notifRef = useRef(null);
  const { darkMode, toggleDarkMode } = useTheme();

  useEffect(() => {
    fetchTodayAttendance();
  }, []);

  const getUserLocation = (showError = false) =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        if (showError) {
          Swal.fire({
            icon: "error",
            title: "Location Not Supported",
            text: "Your browser does not support location access.",
          });
        }
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setUserLocation(location);
          resolve(location);
        },
        (error) => {
          console.error("Location error:", error);
          if (showError) {
            Swal.fire({
              icon: "error",
              title: "Location Required",
              text: "Please enable location access to mark attendance.",
            });
          }
          reject(error);
        },
      );
    });

  const fetchTodayAttendance = async () => {
    try {
      const response = await api.get("/attendance/today");
      if (response.data.success && response.data.data) {
        setAttendance(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch attendance:", error);
    }
  };

  const handleClockInOut = async () => {
    setLoading(true);

    try {
      const location = userLocation || (await getUserLocation(true));
      const response = await api.post("/submit-single-attendance", {
        latitude: location.latitude,
        longitude: location.longitude,
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });

        if (response.data.data) {
          setAttendance(response.data.data);
        }

        setTimeout(() => {
          fetchTodayAttendance();
        }, 500);
      }
    } catch (error) {
      if (!error?.response) {
        return;
      }
      const message =
        error.response?.data?.message || "Failed to mark attendance";
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to logout?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#667eea",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    });

    if (result.isConfirmed) {
      await logout();
      navigate("/login");
    }
  };

  const renderClockButton = () => {
    const designationName =
      typeof user?.designation_name === "string"
        ? user.designation_name
        : (user?.designation_name?.designation_name ?? // nested object
          user?.designation_name?.name ?? // alternate key
          "");

    if (designationName === "Super Admin" || designationName === "Partner") {
      return null;
    }

    if (!attendance || !attendance.time_in) {
      return (
        <button
          className="btn-clock-in"
          onClick={handleClockInOut}
          disabled={loading}
        >
          {loading ? "Processing..." : "Clock In"}
        </button>
      );
    }

    if (attendance.time_in && !attendance.time_out) {
      return (
        <button
          className="btn-clock-out"
          onClick={() => {
            Swal.fire({
              title: "Clock Out?",
              html: `
                        <p style="color:#374151;font-size:14px;margin:0">
                            Are you sure you want to <strong>clock out</strong> now?<br/>
                            <span style="color:#9ca3af;font-size:12px;margin-top:6px;display:block">
                                ${new Date().toLocaleTimeString("en-IN", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })}
                            </span>
                        </p>
                    `,
              icon: "question",
              showCancelButton: true,
              confirmButtonColor: "#ef4444",
              cancelButtonColor: "#6b7280",
              confirmButtonText:
                '<i class="fas fa-sign-out-alt"></i> Yes, Clock Out',
              cancelButtonText: "Cancel",
            }).then((result) => {
              if (result.isConfirmed) {
                handleClockInOut();
              }
            });
          }}
          disabled={loading}
        >
          {loading ? "Processing..." : "Clock Out"}
        </button>
      );
    }

    return null;
  };

  const renderAttendanceTime = () => {
    if (!attendance) return null;

    return (
      <div className="attendance-time-display">
        <span className="time-text">
          In Time:{" "}
          <span className="time-value">{attendance.time_in || "--:--:--"}</span>
        </span>
        <span className="time-separator">|</span>
        <span className="time-text">
          Out Time:{" "}
          <span className="time-value">
            {attendance.time_out || "--:--:--"}
          </span>
        </span>
      </div>
    );
  };

  // ── Live Clock — ticks every second ──
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // ── Fetch Notifications ──
  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds for new notifications
    const poll = setInterval(fetchNotifications, 60000);
    return () => clearInterval(poll);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get("/notifications");
      if (response.data.success) {
        setNotifications(response.data.data);
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      // Silent fail for notifications
    }
  };

  // ── Close dropdown on outside click ──
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // ── Mark All Read ──
  const handleMarkAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      // Silent fail
    }
  };

  // ── Click a single notification ──
  const handleNotifClick = async (notif) => {
    try {
      if (!notif.is_read) {
        await api.post(`/notifications/${notif.id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
      if (notif.url) {
        navigate(notif.url);
        setShowNotifications(false);
      }
    } catch (error) {
      // Silent fail
    }
  };

  // ── Notification icon per type ──
  const getNotifIcon = (type) => {
    const icons = {
      leave: "fa-calendar-times",
      payroll: "fa-money-bill-wave",
      attendance: "fa-fingerprint",
      ticket: "fa-ticket-alt",
      alert: "fa-exclamation-circle",
      info: "fa-info-circle",
      success: "fa-check-circle",
    };
    return icons[type] || "fa-bell";
  };

  const getAvatarUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith("http")) return photoPath;
    if (photoPath.startsWith("/")) return `${BASE_URL}${photoPath}`;
    return `${BASE_URL}/upload/User/${photoPath}`;
  };

  const avatarSrc =
    getAvatarUrl(user?.profile_photo_path) ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      (user?.first_name || "U") + " " + (user?.last_name || "ser"),
    )}&background=6557ff&color=fff`;
    

  return (
    <header className="app-header">
      <div className="header-content">
        {/* Left Side */}
        <div className="header-left">
          {!isDesktop && (
            <button
              type="button"
              className="mobile-sidebar-toggle"
              onClick={onSidebarToggle}
              aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
            >
              <i className={`fas ${isSidebarOpen ? "fa-times" : "fa-bars"}`}></i>
            </button>
          )}
        </div>

        {/* Right Side */}
        <div className="header-right">
          {/* Clock In/Out + Date-Time */}
          <div className="clock-section me-3">
            {renderClockButton()}
            {renderAttendanceTime()}

            {/* ── (b) Date & Time under clock button ── */}
            <div className="header-datetime">
              <span className="header-time">
                {currentDateTime.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
              </span>
              <span className="header-date">
                {currentDateTime.toLocaleDateString("en-IN", {
                  weekday: "short",
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          {/* ── (a) Notification Bell ── */}
          <div className="header-icon-wrapper" ref={notifRef}>
            <button
              className="notif-bell-btn"
              onClick={() => setShowNotifications((prev) => !prev)}
            >
              <i className="fas fa-bell"></i>
              {unreadCount > 0 && (
                <span className="notif-badge">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="dropdown-menu notif-dropdown">
                {/* Dropdown Header */}
                <div className="notif-header">
                  <span className="notif-title">Notifications</span>
                  {unreadCount > 0 && (
                    <button
                      className="notif-mark-all"
                      onClick={handleMarkAllRead}
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                {/* Notification List */}
                <div className="notif-list">
                  {notifications.length === 0 ? (
                    <div className="notif-empty">
                      <i className="fas fa-bell-slash"></i>
                      <p>No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div
                        key={notif.id}
                        className={`notif-item ${!notif.is_read ? "unread" : ""}`}
                        onClick={() => handleNotifClick(notif)}
                      >
                        <div className={`notif-icon-wrap ${notif.type}`}>
                          <i className={`fas ${getNotifIcon(notif.type)}`}></i>
                        </div>
                        <div className="notif-content">
                          <p className="notif-message">{notif.message}</p>
                          <span className="notif-time">{notif.time}</span>
                        </div>
                        {!notif.is_read && (
                          <span className="notif-unread-dot"></span>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                  <div className="notif-footer">
                    <button
                      className="notif-view-all"
                      onClick={() => {
                        navigate("/notifications");
                        setShowNotifications(false);
                      }}
                    >
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="header-icon-wrapper">
            <button
              className="dark-mode-btn"
              onClick={toggleDarkMode}
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              <i className={`fas ${darkMode ? "fa-sun" : "fa-moon"}`}></i>
            </button>
          </div>

          {/* User Profile */}
          <div className="header-icon-wrapper">
            <button
              className="user-profile-btn"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
            >
              <img
                src={avatarSrc}
                alt="User"
                className="user-avatar"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    (user?.first_name || "U") +
                      " " +
                      (user?.last_name || "ser"),
                  )}&background=6557ff&color=fff`;
                }}
              />
            </button>

            {showProfileMenu && (
              <div className="dropdown-menu profile-dropdown">
                <div className="dropdown-header profile-header">
                  <img
                    src={
                      user?.profile_photo_path
                        ? `${BASE_URL}/upload/User/${user?.profile_photo_path}`
                        : "https://ui-avatars.com/api/?name=" +
                          encodeURIComponent(
                            (user?.first_name || "U") +
                              " " +
                              (user?.last_name || "ser"),
                          ) +
                          "&background=6557ff&color=fff"
                    }
                    alt="User"
                    className="profile-avatar"
                  />
                  <div>
                    <h4>
                      {user?.first_name} {user?.last_name}
                    </h4>
                    <p>{user?.email}</p>
                    <p className="user-designation">
                      ({user?.designation_name?.designation_name})
                    </p>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <button
                  className="dropdown-item"
                  onClick={() => navigate("/profile")}
                >
                  <i className="fa-solid fa-user"></i> My Profile
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => navigate("/change-password")}
                >
                  <i className="fa-solid fa-key"></i> Change Password
                </button>
                <button
                  className="dropdown-item"
                  onClick={() => navigate("/my-account")}
                >
                  <i className="fa-solid fa-cog"></i> Account Settings
                </button>
                <div className="dropdown-divider"></div>
                <button
                  className="dropdown-item text-danger"
                  onClick={handleLogout}
                >
                  <i className="fa-solid fa-sign-out-alt"></i> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
